import React, { useContext, useEffect } from "react";
import { WhmStack, WhmTypography } from "@securitize/reactjs-whm";
import translationKeys from "../../../contexts/I18nContext/translationKeys";
import { ProfileContext } from "../../../contexts/ProfileContext";
import { Translate } from "../../Translation/Translate";
import { ErrorPage } from "../../shared/ErrorPage";
import {
  ProfilePermissionType,
  ProfilePermissionTypes,
} from "../../../data/dtos/profile-config.dto";
import { UserVerificationNeeded } from "../information/content-restricted-pages/UserVerificationNeeded";
import { useResolvedPath } from "react-router";

const CONTENT_DOM_ID = "_sec_fe_authorized_members_content";

interface AuthorizedMembersCardProps {
  isPendingActionsLoading: boolean;
  permission: ProfilePermissionType;
}

export const AuthorizedMembersCard = ({
  isPendingActionsLoading,
  permission,
}: AuthorizedMembersCardProps) => {
  const { pathname: currentPath } = useResolvedPath("");
  const { authorizedMembers } = useContext(ProfileContext);

  useEffect(
    () =>
      !isPendingActionsLoading
        ? authorizedMembers.onRenderAuthorizedMembersUI(CONTENT_DOM_ID, false)
        : undefined,
    [authorizedMembers, isPendingActionsLoading],
  );

  if (isPendingActionsLoading) return null;

  if (permission === ProfilePermissionTypes.USER_VERIFICATION_NOT_SUBMITTED) {
    return (
      <UserVerificationNeeded
        isVerificationInReview={false}
        currentPath={currentPath}
      />
    );
  }

  if (permission === ProfilePermissionTypes.USER_VERIFICATION_PENDING) {
    return (
      <UserVerificationNeeded
        isVerificationInReview
        currentPath={currentPath}
      />
    );
  }

  return (
    <WhmStack>
      {authorizedMembers.isError ? (
        <ErrorPage
          errorCode={authorizedMembers.correlationId}
          onRefresh={() => {
            authorizedMembers.onRenderAuthorizedMembersUI(CONTENT_DOM_ID, true);
          }}
        />
      ) : (
        <>
          <WhmTypography variant="h4">
            <Translate
              translationKey={translationKeys.PROFILE_AUTHORIZED_MEMBERS_TITLE}
            />
          </WhmTypography>
          <WhmTypography variant="body2" color="GrayText">
            <Translate
              translationKey={
                translationKeys.PROFILE_AUTHORIZED_MEMBERS_SUBTITLE
              }
            />
          </WhmTypography>
        </>
      )}

      {/*The element where the page is loaded is always in the DOM but hidden when error page is shown*/}
      <WhmStack
        sx={{
          marginTop: "20px",
          display: authorizedMembers.isError ? "none" : "flex",
        }}
        id={CONTENT_DOM_ID}
      />
    </WhmStack>
  );
};
